import Vue from 'vue';
import Vuex from 'vuex';

import { firestoreAction, vuexfireMutations } from 'vuexfire';
import { mergeProjects } from '@/lib/merge-projects';
import { NOTESLIST_INTEGRATION_ENABLED } from '@/lib/URLs';

import actions from './actions';
import getters from './getters';

Vue.use(Vuex);

const QListStore = new Vuex.Store({
    namespaced: true,
    state: {
        projects: null,
        activeProject: null,
        activeProjectData: {
            cues: [],
            notes: [],
            layers: [],
            versions: [],
            scriptMarkers: []
        },
        // noteslist_note_counts/{projectId}: open Notes List notes per cue (A1 cue asterisk). Null until sent.
        notesListCounts: null,
        activeVersion: null,
        activeVersionData: {
            cues: [],
            notes: [],
            layers: [],
            scriptMarkers: []
        },
        activePageSize: null,
        activeProjectProps: null,

        newProjectLoading: false,
        newDuplicateProjectLoading: false,

        invitations: null,
        affiliateInvitations: null,

        users: null,
        activeUser: null,
        // Early-adopter feature flags for the current user (featureFlags/{uid}).
        // null = not a ring member / not yet loaded; hasFlag() coalesces to false.
        featureFlags: null,

        activeTool: null,
        activeDrawTool: null,
        pageScale: 1,
        pageLoader: false,
        pageLoaderLogin: false,
        pageType: null,
        thumbnailView: 'false',
        splitView: null,
        splitPageSync: null,
        splitRatio: null,

        showMode: false,
        showMarginView: false,
        showThumbnailPageUpdate: false
    },
    getters,
    mutations: {
        ...vuexfireMutations,
        SET_PROJECTS(state, projects) {
            state.projects = projects;
        },
        unsetActiveVersion(state) {
            state.activeVersion = null;
            state.activeVersionData = {
                cues: [],
                notes: [],
                layers: [],
                scriptMarkers: []
            };
        },
        unsetActiveProject(state) {
            state.activeProject = null;
            state.activeProjectData = {
                cues: [],
                notes: [],
                layers: [],
                versions: [],
                scriptMarkers: []
            };
            state.notesListCounts = null;
            state.activePageSize = null;
            state.activeProjectProps = null;
        },
        addTemporaryCue(state, cue) {
            state.activeProjectData.cues.push(cue);
        },
        deleteTemporaryCue(state, cue) {
            const index = state.activeProjectData.cues.findIndex(c => c.id === cue.id);
            if (index > -1) {
                state.activeProjectData.cues.splice(index, 1);
            }
        },
        updatePageSize(state, pageSize) {
            state.activePageSize = {
                ...pageSize
            };
        },
        updateProjectProps(state, props) {
            state.activeProjectProps = { ...state.activeProjectProps, ...props };
        }
    },
    actions: {
        setProjectsRef: firestoreAction(({bindFirestoreRef, unbindFirestoreRef, state, commit}, { ref, callback }) => {
            if (state.activeProject) {
                const projectRef = ref.doc(state.activeProject.id);
                unbindFirestoreRef('activeProjectData.cues', projectRef.collection('cues'));
                unbindFirestoreRef('activeProjectData.notes', projectRef.collection('notes'));
                unbindFirestoreRef('activeProjectData.layers', projectRef.collection('layers'));
                unbindFirestoreRef('activeProjectData.versions', projectRef.collection('versions'));
                unbindFirestoreRef('activeProjectData.scriptMarkers', projectRef.collection('scriptMarkers'));
                unbindFirestoreRef('activeProject', projectRef);
                if (NOTESLIST_INTEGRATION_ENABLED) {
                    unbindFirestoreRef('notesListCounts');
                }
                commit('unsetActiveProject');
            }

            if (state.projects === null) {
                bindFirestoreRef('projects', ref).then(() => {
                    (callback)();
                });
            } else {
                ref.get().then((snapshot) => {
                    const newProjects = {};
                    for (const doc of snapshot.docs) {
                        newProjects[doc.id] = doc.data();
                    }
                    
                    // By id: state.projects is an ARRAY after the first binding (mergeProjects explains).
                    commit('SET_PROJECTS', mergeProjects(state.projects, newProjects));
                    (callback)();
                });
            }
        }),
        setActiveProject: firestoreAction(({bindFirestoreRef}, { ref, callback }) => {
            bindFirestoreRef('activeProject', ref).then(() => {
                (callback)();
            });
            bindFirestoreRef('activeProjectData.cues', ref.collection('cues'));
            bindFirestoreRef('activeProjectData.notes', ref.collection('notes'));
            bindFirestoreRef('activeProjectData.layers', ref.collection('layers'));
            bindFirestoreRef('activeProjectData.versions', ref.collection('versions'));
            bindFirestoreRef('activeProjectData.scriptMarkers', ref.collection('scriptMarkers'));
            if (NOTESLIST_INTEGRATION_ENABLED) {
                bindFirestoreRef('notesListCounts', ref.firestore.collection('noteslist_note_counts').doc(ref.id));
            }
        }),
        setUsersRef: firestoreAction(({bindFirestoreRef, state}, { ref, callback }) => {
            if (state.users !== null) {
                (callback)(false);
                return;
            }

            bindFirestoreRef('users', ref).then(() => {
                (callback)();
            });
        }),
        setActiveUser: firestoreAction(({bindFirestoreRef, state}, { ref, callback }) => {
            if (state.activeUser && state.activeUser.uid === ref.id) {
                (callback)(false);
                return;
            }

            // Bind the current user's feature-flag doc alongside their user doc.
            // Most users have no featureFlags/{uid} doc (not ring members) — that
            // binds to null, which hasFlag() treats as "no flags". Reading your
            // own doc is allowed by the featureFlags rule.
            bindFirestoreRef('featureFlags', ref.firestore.collection('featureFlags').doc(ref.id));

            bindFirestoreRef('activeUser', ref).then(() => {
                (callback)();
            });
        }),
        setInvitationsRef: firestoreAction(({bindFirestoreRef}, { ref, callback }) => {
            // Always (re)bind to the requested query. The `invitations` slot is
            // shared across views that each need a DIFFERENT scope — the
            // dashboard binds it to the current user's email invites, while the
            // project/print views bind it to a single project's pending
            // collaborators. A first-wins guard here left the slot stuck on
            // whichever view bound first this page-session, so e.g. opening a
            // project and returning to the dashboard left the Pending Invites
            // panel listening on the project's pid query — new invites only
            // showed after a full refresh. bindFirestoreRef auto-unbinds the
            // previous listener, so rebinding each time is safe and cheap.
            bindFirestoreRef('invitations', ref).then(() => {
                (callback)();
            });
        }),
        setAffiliateInvitationsRef: firestoreAction(({bindFirestoreRef}, { ref, callback }) => {
            // Own slot for affiliate invites (not overloaded onto `invitations`)
            // so the shared-scope binding bug that hit project invites can't
            // recur. Always rebind — bindFirestoreRef auto-unbinds the previous
            // listener, so the dashboard's email-scoped query and the modal's
            // adminUid-scoped query never clobber each other.
            bindFirestoreRef('affiliateInvitations', ref).then(() => {
                if (callback) callback();
            });
        }),
        setActiveVersion: firestoreAction(({bindFirestoreRef}, { ref, callback }) => {
            bindFirestoreRef('activeVersion', ref).then(() => {
                (callback)();
            });
            bindFirestoreRef('activeVersionData.cues', ref.collection('cues'));
            bindFirestoreRef('activeVersionData.notes', ref.collection('notes'));
            bindFirestoreRef('activeVersionData.layers', ref.collection('layers'));
            // #386. Versions saved before that fix have no scriptMarkers
            // subcollection; binding an empty collection is harmless and yields
            // [], which is exactly what such a version should display.
            bindFirestoreRef('activeVersionData.scriptMarkers', ref.collection('scriptMarkers'));
        }),
        unsetActiveVersion: firestoreAction(({unbindFirestoreRef, commit}, { ref, callback }) => {
            unbindFirestoreRef('activeVersionData.cues', ref.collection('cues'));
            unbindFirestoreRef('activeVersionData.notes', ref.collection('notes'));
            unbindFirestoreRef('activeVersionData.layers', ref.collection('layers'));
            unbindFirestoreRef('activeVersionData.scriptMarkers', ref.collection('scriptMarkers'));
            unbindFirestoreRef('activeVersion', ref);
            commit('unsetActiveVersion');
            if (typeof callback === 'function') {
                callback();
            } else {
                console.warn('callback is not a function or is undefined.');
            }
        }),
        ...actions
    }
});

export default QListStore;


// WEBPACK FOOTER //
// ./src/lib/vuex/Store.js